import React, { useEffect, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { Group } from '../../../domain/group';
import { Permission, GROUPED_PERMISSIONS } from '../../../domain/permissions';
import { usePermissions } from '../../../components/auth/usePermissions';

type GroupModalProps = {
    show: boolean;
    onClose: () => void;
    onSave: (name: string, description: string, permissions: Permission[]) => Promise<void>;
    group?: Group;
}

export const GroupModal = ({ show, onClose, onSave, group }: GroupModalProps) => {
    const { hasPermission } = usePermissions();
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [selectedPermissions, setSelectedPermissions] = useState<Permission[]>([]);

    useEffect(() => {
        if (group) {
            setName(group.name);
            setDescription(group.description);
            setSelectedPermissions(group.permissions);
        } else {
            setName('');
            setDescription('');
            setSelectedPermissions([]);
        }
    }, [group, show]);

    const handlePermissionToggle = (permission: Permission) => {
        if (selectedPermissions.includes(permission)) {
            setSelectedPermissions(selectedPermissions.filter(p => p !== permission));
        } else {
            setSelectedPermissions([...selectedPermissions, permission]);
        }
    };

    const handleSave = async () => {
        await onSave(name, description, selectedPermissions);
        onClose();
    };


    return (
        <Modal show={show} onHide={onClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{group ? 'Edytuj grupę' : 'Nowa grupa'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>Nazwa grupy</Form.Label>
                        <Form.Control
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="np. Księgowość"
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Opis</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Krótki opis grupy..."
                        />
                    </Form.Group>
                    {Object.values(GROUPED_PERMISSIONS).map((group) => (
                        <div key={group.name} className="mb-4">
                            <h6 className="border-bottom pb-2 mb-3">{group.name}</h6>
                            <div className="d-flex flex-wrap">
                                {group.permissions
                                    .map((permission) => {
                                        const isRestricted = [
                                            Permission.ADMIN_MANAGE_USERS,
                                            Permission.ADMIN_MANAGE_GROUPS,
                                            Permission.OWNER_ASSIGN_ADMIN
                                        ].includes(permission.id);
                                        const isDisabled = isRestricted && !hasPermission(Permission.OWNER_ASSIGN_ADMIN);

                                        return (
                                            <div key={permission.id} className="w-50 mb-2">
                                                <Form.Check
                                                    type="checkbox"
                                                    id={`permission-${permission.id}`}
                                                    label={permission.label}
                                                    checked={selectedPermissions.includes(permission.id)}
                                                    onChange={() => handlePermissionToggle(permission.id)}
                                                    disabled={isDisabled}
                                                />
                                            </div>
                                        );
                                    })}
                            </div>
                        </div>
                    ))}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>Anuluj</Button>
                <Button variant="primary" onClick={handleSave} disabled={!name}>Zapisz</Button>
            </Modal.Footer>
        </Modal>
    );
};
